import { useState, useEffect, useReducer } from 'react';

const MAX_HISTORY = 10;

const transformInitialState = {
  offsetX: 0,
  offsetY: 0,
  scale: 1,
  opacity: 1,
  history: [],
  future: [],
};

function transformReducer(state, action) {
  switch (action.type) {
    case 'SET_TRANSFORM': {
      const current = { offsetX: state.offsetX, offsetY: state.offsetY, scale: state.scale, opacity: state.opacity };
      const newHistory = [...state.history, current].slice(-MAX_HISTORY);
      return { ...state, ...action.payload, history: newHistory, future: [] };
    }
    case 'RESET_TRANSFORM': {
      return { ...transformInitialState, history: [], future: [] };
    }
    case 'UNDO': {
      if (state.history.length === 0) return state;
      const previous = state.history[state.history.length - 1];
      const newHistory = state.history.slice(0, -1);
      const current = { offsetX: state.offsetX, offsetY: state.offsetY, scale: state.scale, opacity: state.opacity };
      return { ...state, ...previous, history: newHistory, future: [current, ...state.future] };
    }
    case 'REDO': {
      if (state.future.length === 0) return state;
      const next = state.future[0];
      const newFuture = state.future.slice(1);
      const current = { offsetX: state.offsetX, offsetY: state.offsetY, scale: state.scale, opacity: state.opacity };
      return { ...state, ...next, history: [...state.history, current], future: newFuture };
    }
    default:
      return state;
  }
}

export default function useSilhouette() {
  const [silhouetteImage, setSilhouetteImage] = useState(null);
  const [loadedSilhouette, setLoadedSilhouette] = useState(null);
  const [transformState, dispatchTransform] = useReducer(transformReducer, transformInitialState);
  const {
    offsetX: silhouetteOffsetX, offsetY: silhouetteOffsetY,
    scale: silhouetteScale, opacity: silhouetteOpacity,
    history: transformHistory, future: transformFuture,
  } = transformState;

  const handleSilhouetteSelect = (silhouetteSrc) => {
    setSilhouetteImage(silhouetteSrc);
    dispatchTransform({ type: 'RESET_TRANSFORM' });
  };

  const resetSilhouette = () => {
    setSilhouetteImage(null);
    setLoadedSilhouette(null);
  };

  useEffect(() => {
    if (silhouetteImage) {
      const silImgObj = new Image();
      silImgObj.crossOrigin = "Anonymous";
      silImgObj.onload = () => setLoadedSilhouette(silImgObj);
      silImgObj.onerror = () => setLoadedSilhouette(null);
      silImgObj.src = silhouetteImage;
    } else {
      setLoadedSilhouette(null);
    }
  }, [silhouetteImage]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const ctrlOrCmd = isMac ? e.metaKey : e.ctrlKey;
      if (ctrlOrCmd && e.shiftKey && e.key === 'Z') {
        e.preventDefault();
        dispatchTransform({ type: 'REDO' });
      } else if (ctrlOrCmd && e.key === 'z') {
        e.preventDefault();
        dispatchTransform({ type: 'UNDO' });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return {
    silhouetteImage, loadedSilhouette,
    silhouetteOffsetX, silhouetteOffsetY, silhouetteScale, silhouetteOpacity,
    transformHistory, transformFuture, dispatchTransform,
    handleSilhouetteSelect, resetSilhouette,
  };
}
